import { Record } from 'immutable';
import { isUndefined } from 'lodash';
import { LocaleService } from '@sm360/widget-commons-core-services';

import { DATA_TYPES, DEFAULT_MODEL_TRIM_CODE, ITEM_FLAG } from 'commons-vehicle/constants/dataTypes.js';
import { VEHICLE } from 'commons-vehicle/constants/vehicle.js';
import { ODOMETER } from 'commons-vehicle/constants/currencyFormats.js';
import { ONE_DAY_TIMESTAMP } from 'commons-vehicle/constants/dataConversions.js';

import PurchaseMethodsModel from 'commons-vehicle/PurchaseMethods/models/PurchaseMethodsModel.js';
import MultiMediaModel from 'commons-vehicle/Vehicle/models/MultiMediaModel.js';

const VehicleRecord = Record({
    bodyStyle: null,
    bodyStyleCode: '',
    bodyColour: null,
    exteriorColourStatus: 0,
    boxSizeCode: '',
    boxSize: '',
    cabSizeCode: '',
    cabSize: '',
    catalogVehicleId: 0,
    clearance: false,
    configuration: null,
    configurationId: 0,
    configurationToken: '',
    dateSold: null,
    discount: 0,
    doors: 0,
    driveTrain: '',
    driveTrainCode: '',
    engine: '',
    exteriorColour: '',
    favorite: false,
    features: [],
    flags: [],
    fuelCity: 0.0,
    fuelHighway: 0.0,
    fuelType: '',
    id: '',
    interiorColour: null,
    organizationUnitName: '',
    locked: false,
    make: '',
    model: '',
    modelTrim: '',
    modelTrimCode: '',
    modelTrimStyle: [],
    multimedia: new MultiMediaModel(),
    newCarTrimId: 0, // V2.5
    note: '',
    odometer: null,
    purchaseMethods: new PurchaseMethodsModel(),
    recentViewCount: 0,
    seating: 0,
    sellingPrice: 0,
    stockNumber: '',
    sold: false,
    tagLine: '',
    transmission: '',
    trim: '',
    vehicleConditionType: VEHICLE.CONDITIONS.USED,
    vehicleId: 0,
    vin: '',
    warranty: {},
    year: 0,
    discontinued: false,
    online: true,
    showroomVehicleId: '',
    stickerStatusOnOrder: false,
    stickerStatusInTransit: false,
});

class Vehicle extends VehicleRecord {
    isAvailable() {
        return !(this.sold || this.locked || this.discontinued) && this.online;
    }

    getIsVehicleNew() {
        return (
            this.vehicleConditionType === VEHICLE.CONDITIONS.NEW ||
            this.vehicleConditionType === VEHICLE.CONDITIONS.DEMO
        );
    }

    getMake() {
        // return !this.model.includes(this.make) ? this.make : '';
        return this.make || '';
    }

    getModel() {
        return this.model || '';
    }

    getVehicleConditionType() {
        switch (this.vehicleConditionType) {
            case VEHICLE.CONDITIONS.NEW:
                return LocaleService.t('VEHICLE_STATUS.NEW');
            case VEHICLE.CONDITIONS.DEMO:
                return LocaleService.t('VEHICLE_STATUS.DEMO');
            default:
                return LocaleService.t('VEHICLE_STATUS.USED');
        }
    }

    getYearMakeModel() {
        return this.year > 0
            ? LocaleService.t('VEHICLE_MODEL.YEAR_MAKE_MODEL', {
                  year: this.year,
                  make: this.getMake(),
                  model: this.model,
              })
            : '';
    }

    // getYearMakeModelTrim() {
    //     if (this.year === 0) {
    //         return '';
    //     }

    //     /*
    //     const modelTrim = this.configuration !== null ? this.configuration.modelTrim.description : this.modelTrim;

    //     return this.modelTrim && !this.model.includes(this.modelTrim)
    //         ? LocaleService.t('VEHICLE_MODEL.YEAR_MAKE_MODEL_TRIM', {
    //               year: this.year,
    //               make: this.getMake(),
    //               model: this.model,
    //               modelTrim,
    //           })
    //         : LocaleService.t('VEHICLE_MODEL.YEAR_MAKE_MODEL', {
    //               year: this.year,
    //               make: this.getMake(),
    //               model: this.model,
    //           });
    //     */

    //     return LocaleService.t('VEHICLE_MODEL.YEAR_MAKE_MODEL', {
    //         year: this.year,
    //         make: this.getMake(),
    //         model: this.model,
    //     });
    // }

    getYearMakeModelTrim() {
        if (this.year === 0) {
            return '';
        }

        const modelTrim = this?.configuration?.modelTrim?.description || this?.modelTrim || '';

        return modelTrim && !this.model?.includes(modelTrim)
            ? LocaleService.t('VEHICLE_MODEL.YEAR_MAKE_MODEL_TRIM', {
                  year: this.year,
                  make: this.getMake(),
                  model: this.model,
                  modelTrim,
              })
            : LocaleService.t('VEHICLE_MODEL.YEAR_MAKE_MODEL', {
                  year: this.year,
                  make: this.make,
                  model: this.model,
              });
    }

    getYearModel() {
        if (this.year === 0) {
            return '';
        }

        return LocaleService.t('VEHICLE_MODEL.YEAR_MODEL', { year: this.year, model: this.model });
    }

    getYearModelTrim() {
        if (this.year === 0) {
            return '';
        }

        const modelTrim = this.configuration !== null ? this.configuration.modelTrim.description : this.modelTrim;
        return this.modelTrim && this.modelTrim !== this.model
            ? LocaleService.t('VEHICLE_MODEL.YEAR_MODEL_TRIM', {
                  year: this.year,
                  model: this.model,
                  modelTrim,
              })
            : LocaleService.t('VEHICLE_MODEL.YEAR_MODEL', {
                  year: this.year,
                  model: this.model,
              });
    }

    getDiscount() {
        return this.discount > 250 ? this.discount : 0;
    }

    getOdometer() {
        if (!this.odometer) {
            return '';
        }

        const odometer = LocaleService.l(this.odometer, ODOMETER);
        return `${odometer} km`;
    }

    getFuelConsumptionAverage() {
        return Math.round(((this.fuelCity + this.fuelHighway) / 2) * 10) / 10;
    }

    getDriveTrain() {
        if (this.configuration !== null) {
            return this.configuration.driveTrain.longDescription;
        }
        return this.driveTrain;
    }

    getEngine() {
        if (this.configuration !== null) {
            return this.configuration.engine.getSizeSpecifications();
        }
        return this.engine;
    }

    getTransmission() {
        if (this.configuration !== null) {
            return this.configuration.transmission.getTransmission();
        }
        return this.transmission;
    }

    getPaymentCalculatorVehicleParameters() {
        return {
            vehicleConditionType: this.vehicleConditionType,
            year: this.year,
            make: this.make,
            model: this.model,
            bodyStyleCode: this.bodyStyleCode,
            driveTrainCode: this.driveTrainCode,
            odometer: this.odometer,
            sellingPrice: this.sellingPrice,
            vin: this.vin,
            id: this.showroomVehicleId,
            showroomVehicleId: this.showroomVehicleId,
        };
    }

    getDefaultVehicleQuoteItem() {
        return {
            dataType: DATA_TYPES.FEATURE.DATA_TYPE_ID,
            typeID: DATA_TYPES.FEATURE.TYPES.MODEL_TRIM,
            code: DEFAULT_MODEL_TRIM_CODE,
            flag: ITEM_FLAG.SELECTED,
            amounts: {
                retail: this.sellingPrice,
            },
            description: {
                descriptions: [this.model],
            },
        };
    }

    isSoldShown = (soldDaysShown) => {
        if (this.sold) {
            if (isUndefined(soldDaysShown) || soldDaysShown < 0) {
                console.error(
                    "VDP Used - Couldn't determine if Sold vehicle should be displayed, the XMS parameter soldDaysShown is undefined/incorrect"
                );
                return true;
            }
            // If dateSold is undefined, always show vehicle
            if (!this.dateSold) return true;

            // If dateSold is defined and soldDaysShown is 0 then we should never show vehicle
            if (this.dateSold && soldDaysShown === 0) return false;

            // If the current time is beyond the time limit (starting at dateSold): do not show vehicle
            const timeNow = new Date().getTime();
            return timeNow < this.dateSold + soldDaysShown * ONE_DAY_TIMESTAMP;
        }

        // If not sold, always show vehicle
        return true;
    };

    getShowroomVehicle() {
        const {
            bodyColour,
            bodyStyle,
            id,
            vehicleId,
            catalogVehicleId,
            newCarTrimId, // V2.5
            vehicleConditionType,
            vin,
            year,
            make,
            model,
            modelTrim,
            modelTrimCode,
            modelTrimStyle,
            stockNumber,
            sold,
            dateSold,
            odometer,
            configuration,
            configurationId,
            configurationToken,
        } = this;

        const isNewFromConfiguration =
            configuration !== null &&
            (vehicleConditionType === VEHICLE.CONDITIONS.NEW || vehicleConditionType === VEHICLE.CONDITIONS.DEMO);

        return {
            bodyColour,
            bodyStyle,
            id,
            vehicleId: String(vehicleId),
            catalogVehicleId: String(catalogVehicleId),
            newCarTrimId: String(newCarTrimId), // V2.5
            vehicleCondition: vehicleConditionType,
            vin,
            year,
            make,
            model,
            modelTrim,
            modelTrimCode: isNewFromConfiguration ? configuration.modelTrimCode : modelTrimCode,
            modelTrimStyle: isNewFromConfiguration ? configuration.modelTrimStyle : modelTrimStyle,
            configurationID: isNewFromConfiguration ? configuration.id : configurationId,
            configurationToken: configurationToken || '',
            odometer,
            sold,
            dateSold,
            inventory: {
                stock: stockNumber,
            },
        };
    }
}

export default Vehicle;
